import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import NotificationSettingsLinkCard from '../components/settings/NotificationSettingsLinkCard';

const SettingsPage: React.FC = () => {
  const { showTimezoneDebug, setShowTimezoneDebug, timeOffRequestsEnabled, setTimeOffRequestsEnabled } = useSettings();
  const { user } = useAuth();
  
  // Time off approvers state
  const [approvers, setApprovers] = useState({
    operationsApproverUserId: '',
    administrationApproverUserId: '',
    accountingApproverUserId: ''
  });
  const [managers, setManagers] = useState<any[]>([]);
  const [bonusApproverCandidates, setBonusApproverCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Alert system state
  const [alertSystemEnabled, setAlertSystemEnabled] = useState(false);
  const [alertSystemLoading, setAlertSystemLoading] = useState(false);
  const [alertSystemMessage, setAlertSystemMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Timezone debug state
  const [timezoneDebugMessage, setTimezoneDebugMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Time off requests state
  const [timeOffRequestsMessage, setTimeOffRequestsMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Auto clock-out state
  const [autoClockOutEnabled, setAutoClockOutEnabled] = useState(false);
  const [autoClockOutTime, setAutoClockOutTime] = useState('23:00');
  const [autoClockOutMessage, setAutoClockOutMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Missing punches notifications state
  const [missingPunchesNotificationsEnabled, setMissingPunchesNotificationsEnabled] = useState(false);
  const [missingPunchesMessage, setMissingPunchesMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Report sharing state
  const [reportSharingEnabled, setReportSharingEnabled] = useState(false);
  const [reportSharingLoading, setReportSharingLoading] = useState(false);
  const [reportSharingMessage, setReportSharingMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Vacation balance card state
  const [vacationBalanceCardEnabled, setVacationBalanceCardEnabled] = useState(false);
  const [vacationBalanceCardLoading, setVacationBalanceCardLoading] = useState(false);
  const [vacationBalanceCardMessage, setVacationBalanceCardMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Clock-in flexibility state
  const [clockInFlexibilityMinutes, setClockInFlexibilityMinutes] = useState(10);
  const [clockInFlexibilityMessage, setClockInFlexibilityMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Clock out confirmation state
  const [clockOutConfirmationEnabled, setClockOutConfirmationEnabled] = useState(true);
  const [clockOutConfirmationLoading, setClockOutConfirmationLoading] = useState(false);
  const [clockOutConfirmationMessage, setClockOutConfirmationMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Tracking enabled state
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingMessage, setTrackingMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Check if user has admin+ role
  const isAdminOrAbove = user && ['supervisor', 'manager', 'administrator', 'super_user'].includes(user.role);
  const isAdministratorOrAbove = user && ['administrator', 'super_user'].includes(user.role);

  // Load time off approvers and managers
  useEffect(() => {
    if (isAdministratorOrAbove) {
      loadManagers();
      loadTimeOffApprovers();
      loadAlertSystemSetting();
      loadAutoClockOutSetting();
      loadMissingPunchesNotificationsSetting();
      loadClockInFlexibilitySetting();
      loadReportSharingSetting();
      loadVacationBalanceCardSetting();
      loadClockOutConfirmationSetting();
      loadTrackingSetting();
    }
  }, [isAdministratorOrAbove]);

  const loadManagers = async () => {
    try {
      const response = await apiClient.users.getAll();
      if (response.data.success) {
        const managersList = response.data.data.filter((user: any) => 
          ['manager', 'administrator', 'super_user'].includes(user.role)
        );
        setManagers(managersList);
        // Accounting and CEO approvers are not always managers, so they pick from every active non-agent user.
        setBonusApproverCandidates(response.data.data.filter((user: any) => user.isActive !== false && user.role !== 'agent'));
      }
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const loadTimeOffApprovers = async () => {
    try {
      const response = await apiClient.globalSettings.getTimeOffApprovers();
      if (response.data.success) {
        const data = response.data.data;
        setApprovers({
          operationsApproverUserId: data.operationsApprover?.id || '',
          administrationApproverUserId: data.administrationApprover?.id || '',
          accountingApproverUserId: data.accountingApprover?.id || ''
        });
      }
    } catch (error) {
      console.error('Error loading time off approvers:', error);
    }
  };

  const loadAlertSystemSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('missing_employee_alerts_enabled');
      if (response.data.success) {
        setAlertSystemEnabled(response.data.data.value);
      }
    } catch (error) {
      console.error('Error loading alert system setting:', error);
    }
  };

  const loadAutoClockOutSetting = async () => {
    try {
      // Load enabled setting
      const enabledResponse = await apiClient.globalSettings.get('auto_clock_out_enabled');
      if (enabledResponse.data.success) {
        const enabledValue = enabledResponse.data.data.value;
        // Handle both string and boolean values - convert to boolean
        if (typeof enabledValue === 'boolean') {
          setAutoClockOutEnabled(enabledValue);
        } else if (typeof enabledValue === 'string') {
          setAutoClockOutEnabled(enabledValue === 'true' || enabledValue === '1');
        } else {
          setAutoClockOutEnabled(false);
        }
      }
      
      // Load time setting (use getAll to avoid 403 on public GET in older backends)
      try {
        const allResp = await apiClient.globalSettings.getAll();
        if (allResp.data.success) {
          const v = allResp.data.data?.auto_clock_out_time?.value;
          setAutoClockOutTime(typeof v === 'string' ? v : '23:00');
        }
      } catch (e) {
        // Fallback to direct GET if allowed by backend
        const timeResponse = await apiClient.globalSettings.get('auto_clock_out_time');
        if (timeResponse.data.success) {
          const timeValue = (timeResponse as any).data.data.value;
          setAutoClockOutTime(typeof timeValue === 'string' ? timeValue : '23:00');
        }
      }
    } catch (error) {
      console.error('Error loading auto clock-out setting:', error);
    }
  };

  const loadReportSharingSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('report_sharing_enabled');
      if (response.data.success) {
        const value = response.data.data.value;
        // Handle both string and boolean values
        if (typeof value === 'boolean') {
          setReportSharingEnabled(value);
        } else if (typeof value === 'string') {
          setReportSharingEnabled(value === 'true' || value === '1');
        } else {
          setReportSharingEnabled(false);
        }
      }
    } catch (error) {
      console.error('Error loading report sharing setting:', error);
    }
  };

  const loadVacationBalanceCardSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('vacation_balance_card_enabled');
      if (response.data.success) {
        const value = response.data.data?.value;
        if (typeof value === 'boolean') {
          setVacationBalanceCardEnabled(value);
        } else if (typeof value === 'string') {
          setVacationBalanceCardEnabled(value === 'true' || value === '1');
        } else {
          setVacationBalanceCardEnabled(true); // Default to enabled
        }
      }
    } catch (error) {
      console.error('Error loading vacation balance card setting:', error);
    }
  };

  const loadMissingPunchesNotificationsSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('missing_punches_notifications_enabled');
      if (response.data.success) {
        const enabledValue = response.data.data.value;
        if (typeof enabledValue === 'boolean') {
          setMissingPunchesNotificationsEnabled(enabledValue);
        } else if (typeof enabledValue === 'string') {
          setMissingPunchesNotificationsEnabled(enabledValue === 'true' || enabledValue === '1');
        } else {
          setMissingPunchesNotificationsEnabled(false);
        }
      }
    } catch (error) {
      console.error('Error loading missing punches notifications setting:', error);
    }
  };

  const handleTimezoneDebugToggle = async (enabled: boolean) => {
    setTimezoneDebugMessage(null);
    try {
      const response = await apiClient.globalSettings.update('timezone_debug_enabled', enabled);
      if (response.data.success) {
        setShowTimezoneDebug(enabled);
        setTimezoneDebugMessage({ 
          type: 'success', 
          text: `Timezone debug ${enabled ? 'enabled' : 'disabled'} successfully!` 
        });
      } else {
        setTimezoneDebugMessage({ type: 'error', text: response.data.message || 'Failed to update timezone debug setting' });
      }
    } catch (error: any) {
      setTimezoneDebugMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update timezone debug setting' 
      });
    }
  };

  const handleTimeOffRequestsToggle = async (enabled: boolean) => {
    setTimeOffRequestsMessage(null);
    try {
      const response = await apiClient.globalSettings.update('time_off_requests_enabled', enabled);
      if (response.data.success) {
        setTimeOffRequestsEnabled(enabled);
        setTimeOffRequestsMessage({ 
          type: 'success', 
          text: `Time off requests ${enabled ? 'enabled' : 'disabled'} successfully!` 
        });
      } else {
        setTimeOffRequestsMessage({ type: 'error', text: response.data.message || 'Failed to update time off requests setting' });
      }
    } catch (error: any) {
      setTimeOffRequestsMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update time off requests setting' 
      });
    }
  };

  const handleAlertSystemToggle = async (enabled: boolean) => {
    setAlertSystemLoading(true);
    setAlertSystemMessage(null);
    try {
      const response = await apiClient.globalSettings.update('missing_employee_alerts_enabled', enabled);
      if (response.data.success) {
        setAlertSystemEnabled(enabled);
        setAlertSystemMessage({ 
          type: 'success', 
          text: `Alert system ${enabled ? 'enabled' : 'disabled'} successfully!` 
        });
      } else {
        setAlertSystemMessage({ type: 'error', text: response.data.message || 'Failed to update alert system setting' });
      }
    } catch (error: any) {
      setAlertSystemMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update alert system setting' 
      });
    } finally {
      setAlertSystemLoading(false);
    }
  };

  const handleAutoClockOutToggle = async (enabled: boolean) => {
    setAutoClockOutMessage(null);
    try {
      const response = await apiClient.globalSettings.update('auto_clock_out_enabled', enabled);
      if (response.data.success) {
        setAutoClockOutEnabled(enabled);
        setAutoClockOutMessage({ 
          type: 'success', 
          text: `Auto clock-out ${enabled ? 'enabled' : 'disabled'} successfully!` 
        });
      } else {
        setAutoClockOutMessage({ type: 'error', text: response.data.message || 'Failed to update auto clock-out setting' });
      }
    } catch (error: any) {
      setAutoClockOutMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update auto clock-out setting' 
      });
    }
  };

  const handleAutoClockOutTimeChange = async (time: string) => {
    setAutoClockOutMessage(null);
    try {
      // Validate time format (HH:MM)
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(time)) {
        setAutoClockOutMessage({ type: 'error', text: 'Please enter time in HH:MM format (24-hour)' });
        return;
      }

      const response = await apiClient.globalSettings.update('auto_clock_out_time', time);
      if (response.data.success) {
        setAutoClockOutTime(time);
        setAutoClockOutMessage({ 
          type: 'success', 
          text: `Auto clock-out time updated to ${time} successfully!` 
        });
      } else {
        setAutoClockOutMessage({ type: 'error', text: response.data.message || 'Failed to update auto clock-out time' });
      }
    } catch (error: any) {
      setAutoClockOutMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update auto clock-out time' 
      });
    }
  };

  const loadClockInFlexibilitySetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('clock_in_flexibility_minutes');
      if (response.data.success) {
        const value = response.data.data.value;
        const minutes = typeof value === 'string' ? parseInt(value) : (typeof value === 'number' ? value : 10);
        setClockInFlexibilityMinutes(isNaN(minutes) ? 10 : minutes);
      }
    } catch (error) {
      console.error('Error loading clock-in flexibility setting:', error);
    }
  };

  const loadClockOutConfirmationSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('clock_out_confirmation_enabled');
      if (response.data.success) {
        const value = response.data.data.value;
        setClockOutConfirmationEnabled(typeof value === 'boolean' ? value : value === 'true');
      }
    } catch (error) {
      console.error('Error loading clock out confirmation setting:', error);
    }
  };

  const loadTrackingSetting = async () => {
    try {
      const response = await apiClient.globalSettings.get('tracking_enabled');
      if (response.data.success) {
        const value = response.data.data.value;
        setTrackingEnabled(typeof value === 'boolean' ? value : value === 'true');
      }
    } catch (error) {
      console.error('Error loading tracking setting:', error);
    }
  };

  const handleClockOutConfirmationToggle = async (enabled: boolean) => {
    setClockOutConfirmationLoading(true);
    setClockOutConfirmationMessage(null);
    
    try {
      const response = await apiClient.globalSettings.update('clock_out_confirmation_enabled', enabled);
      if (response.data.success) {
        setClockOutConfirmationEnabled(enabled);
        setClockOutConfirmationMessage({
          type: 'success',
          text: `Clock out confirmation ${enabled ? 'enabled' : 'disabled'} successfully.`
        });
      } else {
        throw new Error(response.data.message || 'Failed to update setting');
      }
    } catch (error: any) {
      setClockOutConfirmationMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update clock out confirmation setting'
      });
    } finally {
      setClockOutConfirmationLoading(false);
    }
  };

  const handleTrackingToggle = async (enabled: boolean) => {
    setTrackingLoading(true);
    setTrackingMessage(null);
    
    try {
      const response = await apiClient.globalSettings.update('tracking_enabled', enabled);
      if (response.data.success) {
        setTrackingEnabled(enabled);
        setTrackingMessage({
          type: 'success',
          text: `Tracking ${enabled ? 'enabled' : 'disabled'} successfully. ${enabled ? 'Tracking agents will begin collecting activity data.' : 'All tracking features are now paused and hidden from the UI.'}`
        });
      } else {
        throw new Error(response.data.message || 'Failed to update setting');
      }
    } catch (error: any) {
      setTrackingMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update tracking setting'
      });
      // Revert the toggle on error
      setTrackingEnabled(!enabled);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleClockInFlexibilityChange = async (minutes: number) => {
    setClockInFlexibilityMessage(null);
    
    // Validate: must be between 0 and 60 minutes
    if (minutes < 0 || minutes > 60) {
      setClockInFlexibilityMessage({ 
        type: 'error', 
        text: 'Clock-in flexibility must be between 0 and 60 minutes' 
      });
      return;
    }

    try {
      const response = await apiClient.globalSettings.update('clock_in_flexibility_minutes', minutes.toString());
      if (response.data.success) {
        setClockInFlexibilityMinutes(minutes);
        setClockInFlexibilityMessage({ 
          type: 'success', 
          text: `Clock-in flexibility updated to ${minutes} minutes successfully!` 
        });
      } else {
        setClockInFlexibilityMessage({ type: 'error', text: response.data.message || 'Failed to update clock-in flexibility' });
      }
    } catch (error: any) {
      setClockInFlexibilityMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update clock-in flexibility' 
      });
    }
  };

  const handleReportSharingToggle = async (enabled: boolean) => {
    setReportSharingLoading(true);
    setReportSharingMessage(null);
    
    try {
      const response = await apiClient.globalSettings.update('report_sharing_enabled', enabled ? 'true' : 'false');
      if (response.data.success) {
        setReportSharingEnabled(enabled);
        setReportSharingMessage({
          type: 'success',
          text: `Report sharing ${enabled ? 'enabled' : 'disabled'} successfully.`
        });
      } else {
        throw new Error(response.data.message || 'Failed to update setting');
      }
    } catch (error: any) {
      console.error('Error updating report sharing setting:', error);
      setReportSharingMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to update report sharing setting. Please try again.'
      });
      // Revert the toggle on error
      setReportSharingEnabled(!enabled);
    } finally {
      setReportSharingLoading(false);
    }
  };

  const handleVacationBalanceCardToggle = async (enabled: boolean) => {
    setVacationBalanceCardLoading(true);
    setVacationBalanceCardMessage(null);
    
    try {
      const response = await apiClient.globalSettings.update('vacation_balance_card_enabled', enabled ? 'true' : 'false');
      if (response.data.success) {
        setVacationBalanceCardEnabled(enabled);
        setVacationBalanceCardMessage({
          type: 'success',
          text: `Vacation balance card ${enabled ? 'enabled' : 'disabled'} successfully.`
        });
      } else {
        throw new Error(response.data.message || 'Failed to update setting');
      }
    } catch (error: any) {
      console.error('Error updating vacation balance card setting:', error);
      setVacationBalanceCardMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to update vacation balance card setting. Please try again.'
      });
      // Revert the toggle on error
      setVacationBalanceCardEnabled(!enabled);
    } finally {
      setVacationBalanceCardLoading(false);
    }
  };

  const handleMissingPunchesNotificationsToggle = async (enabled: boolean) => {
    setMissingPunchesMessage(null);
    try {
      const response = await apiClient.globalSettings.update('missing_punches_notifications_enabled', enabled);
      if (response.data.success) {
        setMissingPunchesNotificationsEnabled(enabled);
        setMissingPunchesMessage({ 
          type: 'success', 
          text: `Missing punches email notifications ${enabled ? 'enabled' : 'disabled'} successfully!` 
        });
      } else {
        setMissingPunchesMessage({ type: 'error', text: response.data.message || 'Failed to update missing punches notifications setting' });
      }
    } catch (error: any) {
      setMissingPunchesMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update missing punches notifications setting' 
      });
    }
  };

  const handleSaveApprovers = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await apiClient.globalSettings.setTimeOffApprovers({
        operationsApproverUserId: approvers.operationsApproverUserId,
        administrationApproverUserId: approvers.administrationApproverUserId,
        accountingApproverUserId: approvers.accountingApproverUserId
      });
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Approvers updated successfully!' });
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to update approvers' });
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update time off approvers' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminOrAbove) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Alert severity="warning">
            Access denied. Only supervisors, managers, and administrators can access system settings.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>
      
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Global Display Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={showTimezoneDebug}
                  onChange={(e) => handleTimezoneDebugToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Timezone Debug Information (Global)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, displays timezone debug information on the Clock In/Out screen for ALL employees to help troubleshoot timezone-related issues. This is a global setting that affects all users.
            </Typography>
            
            {/* Timezone Debug Message */}
            {timezoneDebugMessage && (
              <Alert 
                severity={timezoneDebugMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setTimezoneDebugMessage(null)}
              >
                {timezoneDebugMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Time Off Requests Setting */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={timeOffRequestsEnabled}
                  onChange={(e) => handleTimeOffRequestsToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Time Off Requests (Global)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, the "Time Off Requests" menu item will be visible to all users. When disabled, the menu item will be hidden from the navigation.
            </Typography>
            
            {/* Time Off Requests Message */}
            {timeOffRequestsMessage && (
              <Alert 
                severity={timeOffRequestsMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setTimeOffRequestsMessage(null)}
              >
                {timeOffRequestsMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Report Sharing Setting */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Report Sharing
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={reportSharingEnabled}
                  onChange={(e) => handleReportSharingToggle(e.target.checked)}
                  color="primary"
                  disabled={reportSharingLoading}
                />
              }
              label="Enable Report Sharing via Email (Global)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, supervisors and above can share reports via email with attachments (PDF, Excel, CSV). When disabled, the report sharing feature will be unavailable.
            </Typography>
            
            {/* Report Sharing Message */}
            {reportSharingMessage && (
              <Alert 
                severity={reportSharingMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setReportSharingMessage(null)}
              >
                {reportSharingMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Vacation Balance Card Setting */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vacation Balance Card
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={vacationBalanceCardEnabled}
                  onChange={(e) => handleVacationBalanceCardToggle(e.target.checked)}
                  color="primary"
                  disabled={vacationBalanceCardLoading}
                />
              }
              label="Show Vacation Balance Card on Time Off Requests Page"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, employees will see a vacation balance card on the Time Off Requests page showing their available vacation days, accrued days, used days, and other vacation information from the HR system. When disabled, the card will be hidden.
            </Typography>
            
            {/* Vacation Balance Card Message */}
            {vacationBalanceCardMessage && (
              <Alert 
                severity={vacationBalanceCardMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setVacationBalanceCardMessage(null)}
              >
                {vacationBalanceCardMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Alert System Setting */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={alertSystemEnabled}
                  onChange={(e) => handleAlertSystemToggle(e.target.checked)}
                  color="primary"
                  disabled={alertSystemLoading}
                />
              }
              label="Enable Missing Employee Alerts (Global)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4 }}>
              When enabled, the system will automatically send alerts to supervisors when employees are late for their scheduled shifts. Configure alert settings in the Alert System management page.
            </Typography>
            
            {/* Alert System Message */}
            {alertSystemMessage && (
              <Alert 
                severity={alertSystemMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setAlertSystemMessage(null)}
              >
                {alertSystemMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Missing Punches Notifications Setting */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Missing Punches Notifications
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={missingPunchesNotificationsEnabled}
                  onChange={(e) => handleMissingPunchesNotificationsToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Missing Punches Email Notifications"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4 }}>
              When enabled, daily email notifications with a list of missing punches will be automatically sent at 8 AM to team leads, supervisors, and managers for their assigned campaigns/departments. The email includes all missing clock-outs, break ends, and lunch ends from the previous day.
            </Typography>
            
            {/* Missing Punches Message */}
            {missingPunchesMessage && (
              <Alert 
                severity={missingPunchesMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setMissingPunchesMessage(null)}
              >
                {missingPunchesMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Auto Clock-Out Setting */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={autoClockOutEnabled}
                  onChange={(e) => handleAutoClockOutToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Auto Clock-Out (Global)"
            />
            
            {autoClockOutEnabled && (
              <Box sx={{ mt: 3, ml: 4 }}>
                <TextField
                  label="Auto Clock-Out Time"
                  type="time"
                  value={autoClockOutTime}
                  onChange={(e) => handleAutoClockOutTimeChange(e.target.value)}
                  inputProps={{
                    step: 300, // 5 minutes
                  }}
                  sx={{ width: 200 }}
                  helperText="Time when employees will be automatically clocked out (in their local timezone)"
                />
              </Box>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4 }}>
              When enabled, the system will automatically clock out employees at the specified time who forgot to clock out. The time applies to each employee's local timezone. When disabled, employees must manually clock out.
            </Typography>
            
            {/* Auto Clock-Out Message */}
            {autoClockOutMessage && (
              <Alert 
                severity={autoClockOutMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setAutoClockOutMessage(null)}
              >
                {autoClockOutMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Clock-In Flexibility Setting */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Clock-In Flexibility
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Clock-In Flexibility (Minutes)"
                type="number"
                value={clockInFlexibilityMinutes}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setClockInFlexibilityMinutes(value);
                  handleClockInFlexibilityChange(value);
                }}
                inputProps={{
                  min: 0,
                  max: 60,
                  step: 1
                }}
                sx={{ width: 200 }}
                helperText="Number of minutes before scheduled time employees can clock in (0-60 minutes)"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Allows employees to clock in early before their scheduled start time. For example, if set to 10 minutes and an employee's schedule starts at 9:00 AM, they can clock in starting at 8:50 AM. This provides flexibility for employees who arrive early.
            </Typography>
            
            {/* Clock-In Flexibility Message */}
            {clockInFlexibilityMessage && (
              <Alert 
                severity={clockInFlexibilityMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setClockInFlexibilityMessage(null)}
              >
                {clockInFlexibilityMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Clock Out Confirmation Setting */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Clock Out Confirmation
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={clockOutConfirmationEnabled}
                  onChange={(e) => handleClockOutConfirmationToggle(e.target.checked)}
                  disabled={clockOutConfirmationLoading}
                  color="primary"
                />
              }
              label="Enable Clock Out Confirmation Dialog"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4 }}>
              When enabled, users will see a confirmation dialog asking "Are you sure you want to clock out for the day in this campaign?" before clocking out. This helps prevent accidental clock outs. When disabled, users can clock out directly without confirmation.
            </Typography>
            
            {/* Clock Out Confirmation Message */}
            {clockOutConfirmationMessage && (
              <Alert 
                severity={clockOutConfirmationMessage.type} 
                sx={{ mt: 2 }}
                onClose={() => setClockOutConfirmationMessage(null)}
              >
                {clockOutConfirmationMessage.text}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Tracking Setting - Administrator+ Only */}
        {isAdministratorOrAbove && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity Tracking System
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={trackingEnabled}
                    onChange={(e) => handleTrackingToggle(e.target.checked)}
                    disabled={trackingLoading}
                    color="primary"
                  />
                }
                label="Enable Tracking (Windows Agent & Activity Monitoring)"
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4 }}>
                <strong>Feature:</strong> When enabled, the Windows Agent will collect user activity data (mouse/keyboard events, idle detection) and the tracking system will be active. All tracking features (device inventory, activity reports, employee activity UI) will be visible and functional. When disabled, all tracking features are paused, hidden from the UI, and agents will stop collecting activity data. Only administrators and above can manage this setting.
              </Typography>
              
              {!trackingEnabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Tracking is currently disabled.</strong> All tracking features are paused. Windows Agents will not collect activity data, and tracking-related UI elements are hidden from employees.
                </Alert>
              )}
              
              {/* Tracking Message */}
              {trackingMessage && (
                <Alert 
                  severity={trackingMessage.type} 
                  sx={{ mt: 2 }}
                  onClose={() => setTrackingMessage(null)}
                >
                  {trackingMessage.text}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Time Off Approvers Section - Only for Administrators and Super Users */}
      {isAdministratorOrAbove && (
        <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Approvers
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {message && (
                <Alert 
                  severity={message.type} 
                  sx={{ mb: 2 }}
                  onClose={() => setMessage(null)}
                >
                  {message.text}
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Operations Department Approver</InputLabel>
                    <Select
                      value={approvers.operationsApproverUserId || ''}
                      onChange={(e) => setApprovers(prev => ({
                        ...prev,
                        operationsApproverUserId: e.target.value
                      }))}
                      label="Operations Department Approver"
                    >
                      {managers.map((manager) => (
                        <MenuItem key={manager.id} value={manager.id}>
                          {manager.firstName} {manager.lastName} ({manager.email}) - {manager.role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select the manager who will approve Operations Department requests
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Administration Department Approver</InputLabel>
                    <Select
                      value={approvers.administrationApproverUserId || ''}
                      onChange={(e) => setApprovers(prev => ({
                        ...prev,
                        administrationApproverUserId: e.target.value
                      }))}
                      label="Administration Department Approver"
                    >
                      {managers.map((manager) => (
                        <MenuItem key={manager.id} value={manager.id}>
                          {manager.firstName} {manager.lastName} ({manager.email}) - {manager.role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select the manager who will approve Administration Department requests
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Accounting Department Approver (bonuses)</InputLabel>
                    <Select
                      value={approvers.accountingApproverUserId || ''}
                      onChange={(e) => setApprovers(prev => ({ ...prev, accountingApproverUserId: e.target.value }))}
                      label="Accounting Department Approver (bonuses)"
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {bonusApproverCandidates.map((candidate) => (
                        <MenuItem key={candidate.id} value={candidate.id}>
                          {candidate.firstName} {candidate.lastName} ({candidate.email}) - {candidate.role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Confirms that the customer paid TEKS for a bonus. Not used for time off.
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveApprovers}
                  disabled={loading}
                  sx={{ mr: 2 }}
                >
                  {loading ? 'Saving...' : 'Save Approvers'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={loadTimeOffApprovers}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>Time off:</strong> requests require approval from both departments in sequence. 
                Administration must approve first, then Operations. Only the selected managers can approve requests for their respective departments.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Bonuses:</strong> supervisors and above approve first, then Accounting, then Administration, with no step skipped. Each
                step only works for the person selected here (a super user can approve any step). The President approves last, in TOGO OPS, and
                the answer shows in the Bonus Tracker. Every approval records who did it and when. The list of bonus categories is managed under
                Organization &gt; Bonus Categories.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {isAdministratorOrAbove && (
        <Box sx={{ mt: 3 }}>
          <Grid container>
            <NotificationSettingsLinkCard />
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default SettingsPage;
